import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import AdminSidebar from './components/AdminSidebar';
import AdminBreadcrumb from './components/AdminBreadcrumb';
import Home from './pages/Home';
import QuemSomos from './pages/QuemSomos';
import DiaDeCampo from './pages/DiaDeCampo';
import NewsListing from './pages/NewsListing';
import NewsDetail from './pages/NewsDetail';
import ProjectListing from './pages/ProjectListing';
import ProjectDetail from './pages/ProjectDetail';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import NewsForm from './pages/NewsForm';
import CategoryList from './pages/CategoryList';
import CategoryForm from './pages/CategoryForm';
import ProjectList from './pages/ProjectList';
import ProjectForm from './pages/ProjectForm';
import QuotationsList from './pages/QuotationsList';
import QuotationForm from './pages/QuotationForm';
import SocialDashboard from './pages/SocialDashboard';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './styles/global.css';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const scrollToHash = () => {
        const el = document.getElementById(hash.replace('#', ''));
        if (el) {
          const header = document.querySelector('header') as HTMLElement | null;
          const offset = header ? header.offsetHeight : 0;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
          return true;
        }
        return false;
      };

      // Tenta imediatamente; se falhar (página ainda renderizando), tenta depois
      if (!scrollToHash()) {
        setTimeout(scrollToHash, 300);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

// Layout component
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="d-flex flex-column min-vh-100">
      <Header />
      <main className="flex-grow-1">{children}</main>
      <Footer />
    </div>
  );
};

// Guard: redireciona para login se sem sessão; viewer só acessa /admin/social
const AdminOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('admin_token');
  const role = localStorage.getItem('admin_role');
  if (!token) return <Navigate to="/admin/login" replace />;
  if (role === 'viewer') return <Navigate to="/admin/social" replace />;
  return <>{children}</>;
};

// Admin layout with sidebar and breadcrumb
const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/admin/login';

  if (isLoginPage) {
    return <div className="min-vh-100">{children}</div>;
  }

  return (
    <div className="d-flex min-vh-100" style={{ background: '#f8f9fa' }}>
      <AdminSidebar />
      <div style={{ marginLeft: '280px', flex: 1, padding: '2rem' }}>
        <AdminBreadcrumb />
        {children}
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Public routes with layout */}
        <Route
          path="/"
          element={
            <Layout>
              <Home />
            </Layout>
          }
        />
        <Route
          path="/quem-somos"
          element={
            <Layout>
              <QuemSomos />
            </Layout>
          }
        />
        <Route
          path="/dia-de-campo"
          element={
            <Layout>
              <DiaDeCampo />
            </Layout>
          }
        />
        <Route
          path="/noticias"
          element={
            <Layout>
              <NewsListing />
            </Layout>
          }
        />
        <Route
          path="/noticias/:slug"
          element={
            <Layout>
              <NewsDetail />
            </Layout>
          }
        />
        <Route
          path="/projetos"
          element={
            <Layout>
              <ProjectListing />
            </Layout>
          }
        />
        <Route
          path="/projetos/:slug"
          element={
            <Layout>
              <ProjectDetail />
            </Layout>
          }
        />
        <Route
          path="/contato"
          element={<Navigate to="/#contato" replace />}
        />

        {/* Admin routes without layout */}
        <Route
          path="/admin/login"
          element={
            <AdminLayout>
              <AdminLogin />
            </AdminLayout>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminLayout>
              <AdminOnly><AdminDashboard /></AdminOnly>
            </AdminLayout>
          }
        />
        <Route
          path="/admin/noticias/criar"
          element={
            <AdminLayout>
              <AdminOnly><NewsForm /></AdminOnly>
            </AdminLayout>
          }
        />
        <Route
          path="/admin/noticias/editar/:id"
          element={
            <AdminLayout>
              <AdminOnly><NewsForm /></AdminOnly>
            </AdminLayout>
          }
        />
        <Route
          path="/admin/categorias"
          element={
            <AdminLayout>
              <AdminOnly><CategoryList /></AdminOnly>
            </AdminLayout>
          }
        />
        <Route
          path="/admin/categorias/criar"
          element={
            <AdminLayout>
              <AdminOnly><CategoryForm /></AdminOnly>
            </AdminLayout>
          }
        />
        <Route
          path="/admin/categorias/editar/:id"
          element={
            <AdminLayout>
              <AdminOnly><CategoryForm /></AdminOnly>
            </AdminLayout>
          }
        />
        <Route
          path="/admin/projetos"
          element={
            <AdminLayout>
              <AdminOnly><ProjectList /></AdminOnly>
            </AdminLayout>
          }
        />
        <Route
          path="/admin/projetos/criar"
          element={
            <AdminLayout>
              <AdminOnly><ProjectForm /></AdminOnly>
            </AdminLayout>
          }
        />
        <Route
          path="/admin/projetos/editar/:id"
          element={
            <AdminLayout>
              <AdminOnly><ProjectForm /></AdminOnly>
            </AdminLayout>
          }
        />
        <Route
          path="/admin/quotations"
          element={
            <AdminLayout>
              <AdminOnly><QuotationsList /></AdminOnly>
            </AdminLayout>
          }
        />
        <Route
          path="/admin/quotations/new"
          element={
            <AdminLayout>
              <AdminOnly><QuotationForm /></AdminOnly>
            </AdminLayout>
          }
        />
        <Route
          path="/admin/social"
          element={
            <AdminLayout>
              <SocialDashboard />
            </AdminLayout>
          }
        />

        {/* 404 route */}
        <Route
          path="*"
          element={
            <Layout>
              <div className="container py-5 text-center">
                <h1>404</h1>
                <p>Página não encontrada</p>
                <a href="/" className="btn btn-primary">
                  Voltar para Home
                </a>
              </div>
            </Layout>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
